import { useMemo, useState } from 'react';
import { interactionResponse } from '@zillow/yield-callback';
import { Link } from '@/src/i18n/navigation';
import { useTranslations } from 'next-intl';
import { Button, CloseButton, Divider, Drawer, List } from '@zillow/constellation-10';
import { css } from '@/styled-system/css';
import AppleBadge from '@/src/components/ui/icons/AppleBadge';
import AndroidBadge from '@/src/components/ui/icons/AndroidBadge';
import { hamburgerAppBadges } from '@/src/components/ui/Footer/footerLinks';
import { useCurrentArea } from '@/src/lib/hooks/query/useCurrentArea';
import { shouldDisableAppRouterPrefetch } from '@/src/lib/router-compat/prefetch';
import { FeedbackModal } from './FeedbackModal';

type HamburgerMenuProps = {
  id: string;
  isOpen: boolean;
  onClose: () => void;
};

const HELP_URL = 'https://help.hotpads.com/hc/en-us';

export default function HamburgerMenu({ id, isOpen, onClose }: HamburgerMenuProps) {
  const t = useTranslations('nav');
  const [isFeedbackOpen, setIsFeedbackOpen] = useState(false);
  const { data: currentArea } = useCurrentArea();
  const marketResourceId = currentArea?.resourceId;

  // Market known (['area','current'] is seeded on SRP/HDP/BDP — deliberately NOT the homepage's
  // IP-resolved ['area','nearMe'] seed, so crawlers' arbitrary IP locations never geo the homepage
  // menu): geo SRP links, with no "All rentals"
  // item because apartments-for-rent is the unfiltered base SRP (HP-8484). Otherwise every
  // property-type *-near-me slug from the BE search-slug registry (GET /web/v3/search/slugs):
  // All rentals first, then by national listing volume (numUnits sampled across 10 large metros,
  // 2026-07).
  const propertyTypeLinks = useMemo(
    () =>
      marketResourceId
        ? [
            { href: `/${marketResourceId}/apartments-for-rent`, label: t('apartments') },
            { href: `/${marketResourceId}/condos-for-rent`, label: t('condos') },
            { href: `/${marketResourceId}/houses-for-rent`, label: t('houses') },
            { href: `/${marketResourceId}/townhomes-for-rent`, label: t('townhomes') },
            { href: `/${marketResourceId}/duplexes-for-rent`, label: t('duplexes') },
            { href: `/${marketResourceId}/rooms-for-rent`, label: t('rooms') },
          ]
        : [
            { href: '/rentals-near-me', label: t('allRentals') },
            { href: '/apartments-near-me', label: t('apartments') },
            { href: '/condos-near-me', label: t('condos') },
            // Not /homes-for-rent-near-me, which 404s despite HP-8001 listing it.
            { href: '/houses-near-me', label: t('houses') },
            { href: '/townhomes-near-me', label: t('townhomes') },
            { href: '/duplexes-near-me', label: t('duplexes') },
            { href: '/rooms-for-rent-near-me', label: t('rooms') },
          ],
    [marketResourceId, t],
  );

  const utilityLinks = useMemo(
    () => [
      { href: '/list-your-rental', label: t('listYourRental') },
      { href: '/pay-your-rent', label: t('payYourRent') },
    ],
    [t],
  );

  const closeAfterInteraction = async () => {
    await interactionResponse();
    onClose();
  };

  const handleSendFeedback = async () => {
    await interactionResponse();
    setIsFeedbackOpen(true);
    onClose();
  };

  return (
    <>
      {/* Crawler-visible copy of the drawer links: the Drawer mounts nothing into the DOM until opened (HP-8501). */}
      <nav hidden>
        <ul>
          {[...propertyTypeLinks, ...utilityLinks].map((item) => (
            <li key={item.href}>
              <Link href={item.href as React.ComponentProps<typeof Link>['href']} prefetch={false}>
                {item.label}
              </Link>
            </li>
          ))}
          <li>
            <a href={HELP_URL}>{t('help')}</a>
          </li>
        </ul>
      </nav>
      {/* Composable parts, not the shorthand Drawer: the shorthand always draws a top divider and
          cannot name the dialog without a header. A close-button click and the Floating UI
          dismissals already yield in Constellation before onOpenChange runs. Unlike our Modals,
          the menu keeps Constellation's default outside press (a backdrop tap closes it on touch
          too): it holds no state, so the Android-gesture guard in useModalDismissProps buys
          nothing here. */}
      <Drawer.Root
        open={isOpen}
        onOpenChange={(nextOpen) => {
          if (!nextOpen) onClose();
        }}
        placement="left"
      >
        <Drawer.Portal>
          <Drawer.Backdrop scrim>
            <Drawer.Content id={id} aria-label={t('navigationMenu')} css={drawerContent}>
              <Drawer.CloseButton css={drawerClose}>
                <CloseButton label={t('closeMenu')} title={t('closeMenu')} />
              </Drawer.CloseButton>
              <Drawer.Body>
                <nav>
                  <List.Root appearance="none">
                    {propertyTypeLinks.map((item, index) => (
                      <List.Item key={item.href}>
                        <NavLinkButton
                          href={item.href}
                          onClose={closeAfterInteraction}
                          testId={index === 0 ? 'nav-drawer-link-search' : undefined}
                        >
                          {item.label}
                        </NavLinkButton>
                      </List.Item>
                    ))}
                  </List.Root>
                  <Divider />
                  <List.Root appearance="none">
                    {utilityLinks.map((item) => (
                      <List.Item key={item.href}>
                        <NavLinkButton
                          href={item.href}
                          onClose={closeAfterInteraction}
                          testId={item.href === '/list-your-rental' ? 'nav-drawer-link-list-rental' : undefined}
                        >
                          {item.label}
                        </NavLinkButton>
                      </List.Item>
                    ))}
                    <List.Item>
                      <NavActionButton onClick={handleSendFeedback}>{t('sendFeedback')}</NavActionButton>
                    </List.Item>
                    <List.Item>
                      <NavExternalLinkButton href={HELP_URL}>{t('help')}</NavExternalLinkButton>
                    </List.Item>
                  </List.Root>
                </nav>
              </Drawer.Body>
              <Divider />
              <Drawer.Footer>
                <div className={badgeRow}>
                  <a
                    href={hamburgerAppBadges.ios.href}
                    target="_blank"
                    rel="noopener noreferrer nofollow"
                    className={css({ display: 'inline-flex' })}
                  >
                    {/* eslint-disable-next-line hotpads/no-magic-numbers -- design-specific dimension; follow-up tokenization */}
                    <AppleBadge className={css({ height: '36px', width: 'auto' })} />
                  </a>
                  <a
                    href={hamburgerAppBadges.android.href}
                    target="_blank"
                    rel="noopener noreferrer nofollow"
                    className={css({ display: 'inline-flex' })}
                  >
                    {/* eslint-disable-next-line hotpads/no-magic-numbers -- design-specific dimension; follow-up tokenization */}
                    <AndroidBadge className={css({ height: '36px', width: 'auto' })} />
                  </a>
                </div>
              </Drawer.Footer>
            </Drawer.Content>
          </Drawer.Backdrop>
        </Drawer.Portal>
      </Drawer.Root>
      <FeedbackModal isOpen={isFeedbackOpen} onClose={() => setIsFeedbackOpen(false)} />
    </>
  );
}

const menuButtonAlign = css.raw({ justifyContent: 'flex-start' });

// Constellation puts Drawer.CloseButton at the top inline-end corner; the menu keeps it at the
// top inline-start corner, on the same gutter as the Modal header padding.
const drawerClose = css.raw({
  insetBlockStart: 'chrome.gutter',
  insetInlineStart: 'chrome.gutter',
  insetInlineEnd: 'auto',
});

// Constellation paints Drawer.Content with bg.elevated.mediumShadow, which is lighter than the
// Modal's bg.elevated.sheet in dark mode. The menu keeps the Modal surface, as it had before it
// moved off Modal.
const drawerContent = css.raw({ backgroundColor: 'bg.elevated.sheet' });

const badgeRow = css({
  alignItems: 'center',
  display: 'flex',
  gap: 'tighter',
  inlineSize: '100%',
  justifyContent: 'center',
});

function NavLinkButton({
  href,
  onClose,
  children,
  testId,
}: {
  href: string;
  onClose: () => void;
  children: React.ReactNode;
  testId?: string;
}) {
  return (
    <Button asChild tone="neutral" emphasis="bare" fluid css={menuButtonAlign}>
      <Link
        href={href as React.ComponentProps<typeof Link>['href']}
        prefetch={shouldDisableAppRouterPrefetch(href) ? false : undefined}
        onClick={onClose}
        data-testid={testId}
      >
        {children}
      </Link>
    </Button>
  );
}

function NavActionButton({ onClick, children }: { onClick: () => void; children: React.ReactNode }) {
  return (
    <Button type="button" tone="neutral" emphasis="bare" fluid css={menuButtonAlign} onClick={onClick}>
      {children}
    </Button>
  );
}

function NavExternalLinkButton({ href, children }: { href: string; children: React.ReactNode }) {
  return (
    <Button asChild tone="neutral" emphasis="bare" fluid css={menuButtonAlign}>
      <a href={href} target="_blank" rel="noopener noreferrer">
        {children}
      </a>
    </Button>
  );
}
